import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Snackbar } from '@mui/material';
import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import styles from './SnackBar.module.scss';

/**
 * Props for configuring a snackbar notification
 */
type SnackBarProps = {
  /** Main message displayed in the snackbar */
  message: string;
  /** Visual style of the snackbar - success (green) or error (red) */
  severity: 'success' | 'error';
  /** Optional title displayed above the message */
  title?: string;
};

export const SnackBarContext = React.createContext<
  | {
      showSnackBar: (props: SnackBarProps) => void;
    }
  | undefined
>(undefined);

/**
 * Provider component for snackbar notifications.
 * Manages the state and display of notification toasts throughout the application.
 * Notifications auto-hide after 5 seconds and appear in the top-right corner.
 *
 * @param {PropsWithChildren} props - React children to be wrapped by the provider
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <SnackBarProvider>
 *       <YourComponents />
 *     </SnackBarProvider>
 *   );
 * }
 * ```
 *
 * @see useSnackBar - Hook to access snackbar functionality
 */
export function SnackBarProvider({ children }: PropsWithChildren) {
  const [snackBar, setSnackBar] = useState<SnackBarProps | null>(null);

  const showSnackBar = useCallback((props: SnackBarProps) => {
    setSnackBar(props);
  }, []);

  const close = useCallback(() => {
    setSnackBar(null);
  }, []);

  const value = useMemo(() => ({ showSnackBar, close }), [showSnackBar, close]);

  return (
    <SnackBarContext.Provider value={value}>
      {children}
      {snackBar && (
        <Snackbar
          open={Boolean(snackBar)}
          onClose={close}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            severity={snackBar.severity}
            variant="filled"
            iconMapping={{
              success: <FontAwesomeIcon icon={faCircleCheck} size="1x" />,
              error: <FontAwesomeIcon icon={faCircleXmark} size="1x" />,
            }}
          >
            <div className={styles.snackBarAlert}>
              {snackBar.title && <strong>{snackBar.title}</strong>}
              {snackBar.message}
            </div>
          </Alert>
        </Snackbar>
      )}
    </SnackBarContext.Provider>
  );
}
